var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchUsers, buildUserSearchQuery } from "../api/userService";
import { isValidUuid } from "../utils/uuidUtils";
import { isUserListRoleFilter, } from "../utils/nkRoles";
var PAGE_SIZE = 50;
/**
 * The shape of an authority type code (a RecordType name or a vocabulary type code, at most
 * 64 characters like the stored column). Only a value of this shape is read from the URL
 * (CZNA-1610).
 */
var AUTHORITY_TYPE_CODE = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * CZNA-1610 — the authority types of the URL: repeated and/or comma-separated, trimmed,
 * without blanks and duplicates, of the shape of a code. They are NOT checked against the
 * registry: a well-formed code the registry does not name (removed, or a typo in a shared
 * link) stays in the filter, where the list page shows it by its raw code and the server
 * matches it to nothing. Dropping it would silently turn a narrowing into no narrowing —
 * `roles=NK_SUPERVISOR&authorityTypes=<unknown>` would list every Supervizor NK.
 */
var parseAuthorityTypes = function (params) {
    return params
        .getAll("authorityTypes")
        .flatMap(function (value) { return value.split(","); })
        .map(function (code) { return code.trim(); })
        .filter(function (code) { return code !== ""; })
        .filter(function (code, i, codes) { return codes.indexOf(code) === i; })
        .filter(function (code) { return AUTHORITY_TYPE_CODE.test(code); });
};
/**
 * Hook for users list page with search, status filtering, and pagination.
 * Builds CQL queries with proper escaping for safe search.
 * Search is triggered explicitly via handleSearch (form submit / Search button).
 * Filter state is persisted to URL search params so it survives navigation.
 */
/**
 * Maps visible column names to CQL entity field names for sorting. The organization column
 * sorts by sigla, then name — the name orders organizations without a sigla (CZNA-1588).
 */
var SORT_COLUMN_MAP = {
    name: "lastName",
    email: "email",
    organization: ["organization.sigla", "organization.name"],
    status: "status",
    createdAt: "createdAt",
};
var useUserList = function () {
    var ky = useOkapiKy();
    var location = useLocation();
    var history = useHistory();
    // Parse initial filter values from URL search params (read once on mount)
    var initialParams = useMemo(function () {
        var _a;
        var params = new URLSearchParams(location.search);
        var organization = params.get("organization") || "";
        return {
            query: params.get("query") || "",
            status: params.get("status") || "ACTIVE",
            // Only a role the filter offers is taken from the URL (CZNA-1598).
            roles: ((_a = params.get("roles")) !== null && _a !== void 0 ? _a : "")
                .split(",")
                .filter(isUserListRoleFilter),
            // CZNA-1610: authority types narrowing the NK roles.
            authorityTypes: parseAuthorityTypes(params),
            // Only an organization id is taken from the URL — anything else would reach the
            // CQL query (CZNA-1588).
            organization: isValidUuid(organization) ? organization : "",
            offset: parseInt(params.get("offset") || "0", 10) || 0,
            sort: params.get("sort") || "name",
            order: params.get("order") || "ascending",
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    var _a = useState(initialParams.query), query = _a[0], setQuery = _a[1];
    var _b = useState(initialParams.query), appliedQuery = _b[0], setAppliedQuery = _b[1];
    var _c = useState(initialParams.status), statusFilter = _c[0], setStatusFilter = _c[1];
    var _d = useState(initialParams.status), appliedStatusFilter = _d[0], setAppliedStatusFilter = _d[1];
    var _f = useState(initialParams.roles), roleFilter = _f[0], setRoleFilter = _f[1];
    var _g = useState(initialParams.roles), appliedRoleFilter = _g[0], setAppliedRoleFilter = _g[1];
    var _h = useState(initialParams.authorityTypes), authorityTypeFilter = _h[0], setAuthorityTypeFilter = _h[1];
    var _j = useState(initialParams.authorityTypes), appliedAuthorityTypeFilter = _j[0], setAppliedAuthorityTypeFilter = _j[1];
    var _k = useState(initialParams.organization), organizationFilter = _k[0], setOrganizationFilter = _k[1];
    var _l = useState(initialParams.organization), appliedOrganizationFilter = _l[0], setAppliedOrganizationFilter = _l[1];
    var _m = useState(initialParams.sort), sortColumn = _m[0], setSortColumn = _m[1];
    var _o = useState(initialParams.order), sortDirection = _o[0], setSortDirection = _o[1];
    var _p = useState(initialParams.offset), pageOffset = _p[0], setPageOffset = _p[1];
    var _q = useState(true), filterPaneIsVisible = _q[0], setFilterPaneIsVisible = _q[1];
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        setAppliedQuery(query);
        setAppliedStatusFilter(statusFilter);
        setAppliedRoleFilter(roleFilter);
        setAppliedAuthorityTypeFilter(authorityTypeFilter);
        setAppliedOrganizationFilter(organizationFilter);
        setPageOffset(0);
    }, [query, statusFilter, roleFilter, authorityTypeFilter, organizationFilter]);
    // Sync applied filter state to URL search params so filters survive a reload and the
    // way back from a user's detail. The list stays mounted while the detail is open (one
    // route with an optional :id), so this re-runs on every move between list and detail and
    // puts the filters back on a URL a navigation dropped them from. It writes to the CURRENT
    // path: forcing the list path closed a deep-linked detail right after it opened (CZNA-1588).
    useEffect(function () {
        var params = new URLSearchParams();
        if (appliedQuery)
            params.set("query", appliedQuery);
        if (appliedStatusFilter !== "ACTIVE")
            params.set("status", appliedStatusFilter);
        if (appliedRoleFilter.length > 0)
            params.set("roles", appliedRoleFilter.join(","));
        if (appliedAuthorityTypeFilter.length > 0)
            params.set("authorityTypes", appliedAuthorityTypeFilter.join(","));
        if (appliedOrganizationFilter)
            params.set("organization", appliedOrganizationFilter);
        if (pageOffset > 0)
            params.set("offset", pageOffset.toString());
        if (sortColumn !== "name")
            params.set("sort", sortColumn);
        if (sortDirection !== "ascending")
            params.set("order", sortDirection);
        var search = params.toString() ? "?".concat(params.toString()) : "";
        if (history.location.search !== search) {
            history.replace({ pathname: history.location.pathname, search: search });
        }
    }, [
        appliedQuery,
        appliedStatusFilter,
        appliedRoleFilter,
        appliedAuthorityTypeFilter,
        appliedOrganizationFilter,
        pageOffset,
        sortColumn,
        sortDirection,
        history,
        location.pathname,
    ]);
    var isDefaultFilters = appliedStatusFilter === "ACTIVE" &&
        appliedRoleFilter.length === 0 &&
        appliedAuthorityTypeFilter.length === 0 &&
        appliedOrganizationFilter === "" &&
        appliedQuery === "";
    var cqlSortField = SORT_COLUMN_MAP[sortColumn] || "lastName";
    var cqlQuery = buildUserSearchQuery(appliedQuery, appliedStatusFilter, cqlSortField, sortDirection, appliedOrganizationFilter);
    // CZNA-1598: the roles travel beside the CQL, so they are part of the key on their own.
    // CZNA-1610: and so do the authority types.
    var rolesParam = appliedRoleFilter.join(",");
    var authorityTypesParam = appliedAuthorityTypeFilter.join(",");
    var _r = useQuery(["czna-users", cqlQuery, rolesParam, authorityTypesParam, pageOffset], function () {
        return fetchUsers(ky, {
            query: cqlQuery,
            roles: appliedRoleFilter,
            authorityTypes: appliedAuthorityTypeFilter,
            offset: pageOffset,
            limit: PAGE_SIZE,
        });
    }, {
        keepPreviousData: true,
    }), data = _r.data, isLoading = _r.isLoading, isFetching = _r.isFetching, error = _r.error;
    var handleClearSearch = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setPageOffset(0);
    }, []);
    var handleClearFilters = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setStatusFilter("ACTIVE");
        setAppliedStatusFilter("ACTIVE");
        setRoleFilter([]);
        setAppliedRoleFilter([]);
        setAuthorityTypeFilter([]);
        setAppliedAuthorityTypeFilter([]);
        setOrganizationFilter("");
        setAppliedOrganizationFilter("");
        setPageOffset(0);
    }, []);
    var handleStatusFilterChange = useCallback(function (value) {
        setStatusFilter(value);
    }, []);
    var handleRoleFilterChange = useCallback(function (role, checked) {
        setRoleFilter(function (prev) {
            return checked ? __spreadArray(__spreadArray([], prev, true), [role], false) : prev.filter(function (r) { return r !== role; });
        });
    }, []);
    var handleAuthorityTypeFilterChange = useCallback(function (code, checked) {
        setAuthorityTypeFilter(function (prev) {
            return checked
                ? prev.includes(code)
                    ? prev
                    : __spreadArray(__spreadArray([], prev, true), [code], false)
                : prev.filter(function (c) { return c !== code; });
        });
    }, []);
    var handleOrganizationFilterChange = useCallback(function (value) {
        setOrganizationFilter(value);
    }, []);
    var handleSort = useCallback(function (_e, _a) {
        var name = _a.name;
        if (name === sortColumn) {
            setSortDirection(function (prev) {
                return prev === "ascending" ? "descending" : "ascending";
            });
        }
        else {
            setSortColumn(name);
            setSortDirection("ascending");
        }
        setPageOffset(0);
    }, [sortColumn]);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var fetchMoreData = useCallback(function (offset) {
        setPageOffset(offset);
    }, []);
    return {
        query: query,
        setQuery: setQuery,
        pageOffset: pageOffset,
        pageSize: PAGE_SIZE,
        statusFilter: statusFilter,
        handleStatusFilterChange: handleStatusFilterChange,
        roleFilter: roleFilter,
        handleRoleFilterChange: handleRoleFilterChange,
        authorityTypeFilter: authorityTypeFilter,
        appliedAuthorityTypeFilter: appliedAuthorityTypeFilter,
        handleAuthorityTypeFilterChange: handleAuthorityTypeFilterChange,
        organizationFilter: organizationFilter,
        handleOrganizationFilterChange: handleOrganizationFilterChange,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        handleSort: handleSort,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        handleSearch: handleSearch,
        handleClearSearch: handleClearSearch,
        handleClearFilters: handleClearFilters,
        isDefaultFilters: isDefaultFilters,
        fetchMoreData: fetchMoreData,
        items: (data === null || data === void 0 ? void 0 : data.items) || [],
        totalRecords: (data === null || data === void 0 ? void 0 : data.totalRecords) || 0,
        isLoading: isLoading,
        isFetching: isFetching,
        error: error,
    };
};
export default useUserList;
